import { translationDetails } from '@paywithextend/localization'
import { defaultLocale, defaultWhiteLabelBrand } from 'src/configurations'
import { ExtendCurrency, ExtendLocales, SupportedBrands } from 'src/enums'
// BASE translation jsons
import enUS from 'src/localization/extend/en-US.json'
import frCA from 'src/localization/extend/fr-CA.json'

export interface LocaleType {
  iso: ExtendLocales
  displayName: string
  jsonTranslations: Record<string, translationDetails>
  currency: ExtendCurrency
  dateFormat: string
  dateString: (data: string[]) => string
  shortDateFormat: string
  shortDateString: (data: string[]) => string
}

function getWhitelabelJson(
  brand: SupportedBrands,
  locale: string,
): Record<string, Record<string, { message: string; description: string }>> {
  const defaultEnglishLocale = 'en-US'
  // If the current brand is the default one, don't send anything back. Just spread an empty object
  if (brand === defaultWhiteLabelBrand) {
    return {}
  }

  // At this time, we only have US and CA as the english (en) locale
  // If a user is en-CA - send them the en-US.json data instead
  // This will change as we get non-US English locales in the codebase, but
  // for now, we will always send the default english US locale back
  if (locale.startsWith('en')) {
    return require(`./${brand}/${defaultEnglishLocale}.json`)
  }

  try {
    return require(`./${brand}/${locale}.json`)
  } catch {
    return require(`./${brand}/${defaultEnglishLocale}.json`)
  }
}

export const getLocaleInfo = (
  locale: string,
  whitelabelBrand = SupportedBrands.Extend,
): LocaleType => {
  // if locale is not in ExtendLocales (ie not supported), set to default
  if (
    !Object.values(ExtendLocales).includes(locale as unknown as ExtendLocales)
  ) {
    locale = defaultLocale
  }

  const brandJson = getWhitelabelJson(whitelabelBrand, locale)

  switch (locale) {
    case ExtendLocales.enCA:
      return {
        iso: ExtendLocales.enCA,
        displayName: 'English (Canada)',
        jsonTranslations: { ...enUS, ...brandJson },
        currency: ExtendCurrency.Cad,
        dateFormat: 'YYYY/MM/DD',
        dateString: (data: string[]): string =>
          data[0] + '-' + data[1] + '-' + data[2],
        shortDateFormat: 'YY/MM',
        shortDateString: (data: string[]): string => data[0] + '-' + data[1],
      }
    case ExtendLocales.frCA:
      return {
        iso: ExtendLocales.frCA,
        displayName: 'French (Canada)',
        jsonTranslations: { ...frCA, ...brandJson },
        currency: ExtendCurrency.Cad,
        dateFormat: 'YYYY/MM/DD',
        dateString: (data: string[]): string =>
          data[0] + '-' + data[1] + '-' + data[2],
        shortDateFormat: 'YY/MM',
        shortDateString: (data: string[]): string => data[0] + '-' + data[1],
      }
    case ExtendLocales.enUS:
    default:
      return {
        iso: ExtendLocales.enUS,
        displayName: 'English (US)',
        jsonTranslations: { ...enUS, ...brandJson },
        currency: ExtendCurrency.Usd,
        dateFormat: 'MM/DD/YYYY',
        dateString: (data: string[]): string =>
          data[2] + '-' + data[0] + '-' + data[1],
        shortDateFormat: 'MM/YY',
        shortDateString: (data: string[]): string => data[1] + '-' + data[0],
      }
  }
}
